import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Settings as SettingsIcon, Save, Plus, Trash2, MonitorCheck, Upload, TriangleAlert } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";

import API from '../api_config';

export default function SettingsPage() {
  const [settings, setSettings] = useState({
    company_name: '',
    company_rut: '',
    company_address: '',
    tax_enabled: true,
    tax_rate: 0.19,
    currency: 'CLP',
    enable_credit_sales: true,
    credit_surcharge_percentage: 0.0,
    receipt_footer: '',
    enable_sii_integration: false
  });
  const [loading, setLoading] = useState(false);
  
  // Registers State
  const [registers, setRegisters] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [newRegisterName, setNewRegisterName] = useState('');
  const [newRegisterPrimaryWarehouse, setNewRegisterPrimaryWarehouse] = useState('');
  const [newRegisterWarehouseIds, setNewRegisterWarehouseIds] = useState([]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
       // toast.error('Error al cargar configuración');
    }
  };

  const fetchRegistersAndWarehouses = async () => {
      try {
          const [regsRes, wareRes] = await Promise.all([
              axios.get(`${API}/pos/registers`),
              axios.get(`${API}/warehouses`)
          ]);
          setRegisters(regsRes.data);
          setWarehouses(wareRes.data);
          setWarehouses(wareRes.data);
          if (wareRes.data.length > 0) {
              setNewRegisterPrimaryWarehouse(wareRes.data[0].id);
              setNewRegisterWarehouseIds([wareRes.data[0].id]);
          }
      } catch (error) {
          console.error("Error loading registers/warehouses", error);
      }
  };

  useEffect(() => {
      fetchRegistersAndWarehouses();
  }, []);

  const handleCreateRegister = async () => {
      if (!newRegisterName) return toast.error("Ingrese un nombre para la caja");
      if (!newRegisterPrimaryWarehouse) return toast.error("Seleccione una bodega principal");

      try {
          const res = await axios.post(`${API}/pos/registers`, {
              name: newRegisterName,
              warehouse_id: newRegisterPrimaryWarehouse,
              warehouse_ids: newRegisterWarehouseIds
          });
          setRegisters([...registers, res.data]);
          setNewRegisterName('');
          setNewRegisterWarehouseIds([newRegisterPrimaryWarehouse]); // Reset to default
          toast.success("Caja creada exitosamente");
      } catch (error) {
          toast.error("Error al crear caja: " + (error.response?.data?.detail || error.message));
      }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success('Configuración guardada exitosamente');
    } catch (error) {
      toast.error('Error al guardar configuración: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <SettingsIcon className="h-8 w-8" />
        <div>
          <h1 className="text-3xl font-bold">Configuración</h1>
          <p className="text-muted-foreground">Configura los parámetros de tu empresa</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Company Profile Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Datos de la Empresa</CardTitle>
            <CardDescription>Información principal de tu empresa (Emisor)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Razón Social / Nombre</Label>
                <Input
                  id="company_name"
                  value={settings.company_name || ''}
                  onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  placeholder="Mi Empresa SpA"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_rut">RUT</Label>
                <Input
                  id="company_rut"
                  value={settings.company_rut || ''}
                  onChange={(e) => setSettings({ ...settings, company_rut: e.target.value })}
                  placeholder="76.123.456-7"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="company_address">Dirección Comercial</Label>
              <Input
                id="company_address"
                value={settings.company_address || ''}
                onChange={(e) => setSettings({ ...settings, company_address: e.target.value })}
                placeholder="Av. Providencia 1234, Oficina 601, Santiago"
              />
            </div>
          </CardContent>
        </Card>

        {/* Tax Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Configuración de Impuestos</CardTitle>
            <CardDescription>Configura el IVA y otros impuestos</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="tax_enabled">Activar IVA</Label>
                <p className="text-sm text-muted-foreground">
                  Habilitar o deshabilitar el cálculo de IVA en las ventas
                </p>
              </div>
              <input
                type="checkbox"
                id="tax_enabled"
                checked={settings.tax_enabled}
                onChange={(e) => setSettings({ ...settings, tax_enabled: e.target.checked })}
                className="h-5 w-5 rounded border-gray-300"
              />
            </div>

            {settings.tax_enabled && (
              <div className="space-y-2">
                <Label htmlFor="tax_rate">Tasa de IVA (%)</Label>
                <Input
                  id="tax_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={settings.tax_rate}
                  onChange={(e) => setSettings({ ...settings, tax_rate: parseFloat(e.target.value) })}
                  placeholder="0.19"
                />
                <p className="text-xs text-muted-foreground">
                  Ejemplo: 0.19 para 19%, 0.21 para 21%
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Credit Sales Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Ventas a Crédito (Fiado)</CardTitle>
            <CardDescription>Configura las ventas a crédito y recargos</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="enable_credit_sales">Habilitar Ventas a Crédito</Label>
                <p className="text-sm text-muted-foreground">
                  Permitir ventas con pago diferido (fiado)
                </p>
              </div>
              <input
                type="checkbox"
                id="enable_credit_sales"
                checked={settings.enable_credit_sales}
                onChange={(e) => setSettings({ ...settings, enable_credit_sales: e.target.checked })}
                className="h-5 w-5 rounded border-gray-300"
              />
            </div>

            {settings.enable_credit_sales && (
              <div className="space-y-2">
                <Label htmlFor="credit_surcharge_percentage">Recargo por Fiado (%)</Label>
                <Input
                  id="credit_surcharge_percentage"
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={settings.credit_surcharge_percentage}
                  onChange={(e) => setSettings({ ...settings, credit_surcharge_percentage: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                />
                <p className="text-xs text-muted-foreground">
                  Porcentaje de recargo sobre el total (incluido IVA). Ejemplo: 5 para 5%
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Gestión de Cajas (POS)</CardTitle>
            <CardDescription>Administra las cajas registradoras disponibles</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
             <div className="flex gap-4 items-start bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg border">
                <div className="flex-1 space-y-2">
                    <Label>Nombre de la Caja</Label>
                    <Input 
                        placeholder="Ej. Caja Principal, Caja 2..." 
                        value={newRegisterName}
                        onChange={(e) => setNewRegisterName(e.target.value)}
                    />
                </div>
                <div className="flex-1 space-y-2">
                    <Label>Bodegas Asignadas</Label>
                     <div className="border rounded-md p-2 h-32 overflow-y-auto bg-background">
                        {warehouses.map(w => (
                            <div key={w.id} className="flex items-center space-x-2 mb-1">
                                <input 
                                    type="checkbox" 
                                    id={`wh-${w.id}`}
                                    checked={newRegisterWarehouseIds.includes(w.id)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setNewRegisterWarehouseIds([...newRegisterWarehouseIds, w.id]);
                                        } else {
                                            setNewRegisterWarehouseIds(newRegisterWarehouseIds.filter(id => id !== w.id));
                                        }
                                    }}
                                    className="h-4 w-4 rounded border-gray-300"
                                />
                                <label htmlFor={`wh-${w.id}`} className="text-sm cursor-pointer flex-1">
                                    {w.name} {w.id === newRegisterPrimaryWarehouse && <span className="text-xs text-muted-foreground">(Principal)</span>}
                                </label>
                            </div>
                        ))}
                     </div>
                     <div className="text-xs text-muted-foreground mt-1">
                        Principal: <select 
                            value={newRegisterPrimaryWarehouse} 
                            onChange={(e) => setNewRegisterPrimaryWarehouse(e.target.value)}
                            className="bg-transparent border-none p-0 text-xs font-semibold focus:ring-0"
                        >
                            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                        </select>
                     </div>
                </div>
                <Button onClick={handleCreateRegister} className="mt-8">
                    <Plus className="h-4 w-4 mr-2" /> Agregar Caja
                </Button>
             </div>

             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {registers.map(reg => (
                    <div key={reg.id} className="border rounded-lg p-4 flex justify-between items-start bg-card">
                        <div>
                            <div className="flex items-center gap-2 font-semibold">
                                <MonitorCheck className="h-4 w-4 text-indigo-500" />
                                {reg.name}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                                Bodega(s): {
                                    (reg.warehouse_ids && reg.warehouse_ids.length > 0) 
                                    ? reg.warehouse_ids.map(id => warehouses.find(w => w.id === id)?.name).filter(Boolean).join(", ")
                                    : warehouses.find(w => w.id === reg.warehouse_id)?.name || 'Desconocida'
                                }
                            </p>
                            <div className={`text-xs mt-2 px-2 py-0.5 rounded-full inline-block ${
                                reg.status === 'open' 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' 
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
                            }`}>
                                {reg.status === 'open' ? '🟢 Abierta' : '🔴 Cerrada'}
                            </div>
                        </div>
                    </div>
                ))}
                {registers.length === 0 && (
                    <br/>
                )}
                 {registers.length === 0 && (
                    <p className="text-sm text-muted-foreground col-span-full text-center py-4">No hay cajas registradas.</p>
                )}
             </div>
          </CardContent>
        </Card>

        {/* Document Settings (Logo & Folios) */}
        <Card>
          <CardHeader>
            <CardTitle>Personalización de Documentos</CardTitle>
            <CardDescription>Configura el logo y folios para Boletas y Facturas</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
             {/* LOGO UPLOAD */}
             <div className="space-y-2">
                <Label>Logo de la Empresa</Label>
                <div className="flex items-center gap-4">
                    {settings.company_logo_url && (
                        <div className="border rounded p-1">
                            <img 
                                src={`${API}${settings.company_logo_url}`} 
                                alt="Logo Empresa" 
                                className="h-16 w-auto object-contain"
                            />
                        </div>
                    )}
                    <div className="flex-1">
                        <Input 
                            type="file" 
                            accept="image/*"
                            onChange={async (e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                
                                const formData = new FormData();
                                formData.append('file', file);
                                
                                try {
                                    // Need company_id, usually in user context or settings? 
                                    // Settings endpoint returns company_id
                                    if(!settings.company_id) return toast.error("No se pudo identificar la empresa");

                                    const res = await axios.post(`${API}/companies/${settings.company_id}/logo`, formData, {
                                        headers: { 'Content-Type': 'multipart/form-data' }
                                    });
                                    setSettings({ ...settings, company_logo_url: res.data.logo_url });
                                    toast.success("Logo subido exitosamente");
                                } catch (error) {
                                    toast.error("Error al subir logo");
                                }
                            }}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Recomendado: PNG o JPG, fondo transparente.</p>
                    </div>
                </div>
             </div>

             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div className="space-y-2">
                    <Label htmlFor="folio_boleta">Folio Actual Boleta</Label>
                    <Input
                        id="folio_boleta"
                        type="number"
                        min="1"
                        value={settings.current_folio_boleta || 1}
                        onChange={(e) => setSettings({ ...settings, current_folio_boleta: parseInt(e.target.value) || 1 })}
                    />
                    <p className="text-xs text-muted-foreground">Siguiente boleta será N° { (settings.current_folio_boleta || 0) + 1 }</p>
                 </div>
                 <div className="space-y-2">
                    <Label htmlFor="folio_factura">Folio Actual Factura</Label>
                    <Input
                        id="folio_factura"
                        type="number"
                        min="1"
                        value={settings.current_folio_factura || 1}
                        onChange={(e) => setSettings({ ...settings, current_folio_factura: parseInt(e.target.value) || 1 })}
                    />
                    <p className="text-xs text-muted-foreground">Siguiente factura será N° { (settings.current_folio_factura || 0) + 1 }</p>
                 </div>
             </div>

             <div className="flex items-center justify-between pt-4 border-t">
                <div className="space-y-0.5">
                    <Label htmlFor="sii_integration">Conexión con SII</Label>
                    <p className="text-sm text-muted-foreground">
                        Habilitar para mostrar el Timbre Electrónico en los documentos PDF.
                    </p>
                </div>
                <input
                    type="checkbox"
                    id="sii_integration"
                    checked={settings.enable_sii_integration || false}
                    onChange={(e) => setSettings({ ...settings, enable_sii_integration: e.target.checked })}
                    className="h-5 w-5 rounded border-gray-300"
                />
             </div>
             
             {settings.enable_sii_integration && (
                <Alert variant="destructive" className="mt-4 bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-900 dark:text-yellow-200">
                    <TriangleAlert className="h-4 w-4 !text-yellow-800 dark:!text-yellow-200" />
                    <AlertTitle>¡Atención!</AlertTitle>
                    <AlertDescription>
                        Advertencia: La conexión real con el SII requiere un desarrollo de certificación específico para cada empresa (Certificado Digital, CAF, Firmas). 
                        Activar esta opción solo muestra una simulación visual del timbre.
                    </AlertDescription>
                </Alert>
             )}
          </CardContent>
        </Card>

        {/* Other Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Otras Configuraciones</CardTitle>
            <CardDescription>Configuraciones generales del sistema</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currency">Moneda</Label>
              <Input
                id="currency"
                value={settings.currency}
                onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                placeholder="CLP"
                maxLength={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="receipt_footer">Pie de Página del Recibo</Label>
              <textarea
                id="receipt_footer"
                value={settings.receipt_footer || ''}
                onChange={(e) => setSettings({ ...settings, receipt_footer: e.target.value })}
                placeholder="Gracias por su compra"
                className="w-full min-h-[80px] px-3 py-2 text-sm rounded-md border border-input bg-background"
              />
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={loading} size="lg">
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Guardando...' : 'Guardar Configuración'}
          </Button>
        </div>
      </div>
    </div>
  );
}
